import type {
    StandalonePackage,
    StandaloneRecommendation,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncStatus } from "@react-hookz/web";
import { useDebouncedEffect } from "@react-hookz/web";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";

import { insightPackagesFilters } from "../../config/filters/insightPackages";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { discoveryFilterStore } from "../../store/filters";
import { InsightPackagesFilterField } from "../../types/filters/insightPackages";
import { IHubContentProvider } from "../../types/providers";
import { isSuccess } from "../../utils/api.utils";
import { getFilterOptions } from "../../utils/filters.utils";
import {
    matchesInsightPackageSelectionsForPackageAPI,
    generateInsightPackageFilterOptionsForPackageAPI,
} from "../../utils/insightPackages.utils";
import {
    getAdvertiserId,
    getMarketplaceId,
    isAccountAgnosticId,
} from "../../utils/permission.utils";
import { logInsightBrowseResults } from "../../utils/telemetry.utils";
import { getInsightIdFromSearchParams } from "../../utils/window.utils";
import { useClientSideLazyLoad } from "../misc/useClientSideLazyLoad";

import { useStandalonePackagesAction } from "./useInsightPackagesAction";

export interface InsightPackagesLoaderProps {
    isLoaderInView?: boolean;
}

// TODO: Remove UnifiedContent type and use StandalonePackage after fully migrate away from CAIRE recommendations.
export type UnifiedContent = StandaloneRecommendation | StandalonePackage;

export function useInsightPackagesLoader({
    isLoaderInView,
}: InsightPackagesLoaderProps): [AsyncStatus, UnifiedContent[], number] {
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);
    const [packages, setPackages] = useState<UnifiedContent[]>([]);
    const insightIdentifier = getInsightIdFromSearchParams();

    const enableSingleIdentifierView = insightIdentifier;

    const [
        setFilterOptions,
        setData,
        fieldSelections,
        contentSubType,
        searchQuery,
    ] = useStore(discoveryFilterStore, (state) => [
        state.setFilterOptions,
        state.setData,
        state.getInsightsFilters().PACKAGES.fieldSelections,
        state.getContentSubType(),
        state.getInsightsFilters()[IHubContentProvider.PACKAGES].searchQuery,
    ]);

    const packageNameList = contentSubType.scope?.split("|");
    const request = {
        advertiserIdType: "SALESFORCE",
        advertiserId: getAdvertiserId()!,
        marketplaceId: getMarketplaceId()!,
        packageNameFilters: packageNameList!,
    };

    const [insightsStatePackageAPI, insightsActionPackageAPI] =
        useStandalonePackagesAction(request);

    const fetchStandalonePackages = useCallback(() => {
        setPackages([]);
        if (isAccountAgnosticId()) {
            return setPackages([]);
        }
        const fetchedPackages = async () => {
            const insightsData = await insightsActionPackageAPI.execute();
            setPackages(insightsData);
        };
        void fetchedPackages();
    }, [insightsActionPackageAPI, setFilterOptions, setData, setPackages]);

    useEffect(() => {
        fetchStandalonePackages();
    }, []);

    useEffect(() => {
        insightPackagesFilters.forEach((filter) => {
            const currentMatchedPackages = packages.filter((insight) => {
                const currentFieldSelections = {
                    ...fieldSelections,
                    [filter.field]: [],
                };
                return matchesInsightPackageSelectionsForPackageAPI(
                    insight as StandalonePackage,
                    currentFieldSelections,
                    searchQuery,
                );
            });
            const { PACKAGED_BRAND, PACKAGED_BROWSE_NODE, PACKAGED_OBJECTIVE } =
                generateInsightPackageFilterOptionsForPackageAPI(
                    currentMatchedPackages as StandalonePackage[],
                );

            if (filter.field === InsightPackagesFilterField.BRAND) {
                setFilterOptions(
                    filter.field,
                    getFilterOptions(PACKAGED_BRAND),
                );
            } else if (
                filter.field === InsightPackagesFilterField.BROWSE_NODE
            ) {
                setFilterOptions(
                    filter.field,
                    getFilterOptions(PACKAGED_BROWSE_NODE),
                );
            } else if (filter.field === InsightPackagesFilterField.OBJECTIVE) {
                setFilterOptions(
                    filter.field,
                    getFilterOptions(PACKAGED_OBJECTIVE),
                );
            }
        });
    }, [packages, fieldSelections, searchQuery, insightIdentifier]);

    const matchedPackages = useMemo(() => {
        return packages.filter((insight) => {
            const matchesSelections =
                matchesInsightPackageSelectionsForPackageAPI(
                    insight as StandalonePackage,
                    fieldSelections,
                    searchQuery,
                );

            if (enableSingleIdentifierView) {
                return (
                    matchesSelections &&
                    insightIdentifier === insight.identifier
                );
            }

            return matchesSelections;
        });
    }, [packages, fieldSelections, searchQuery, insightIdentifier]);

    const [lazyLoadedInsights] = useClientSideLazyLoad(
        matchedPackages,
        isLoaderInView ?? false,
    );

    useDebouncedEffect(
        () => {
            if (isSuccess(insightsStatePackageAPI.status)) {
                setData(IHubContentProvider.PACKAGES, matchedPackages);
                logInsightBrowseResults(
                    matchedPackages.length,
                    useUnifiedSearch,
                );
            }
        },
        [matchedPackages, insightsStatePackageAPI.status],
        500,
    );

    return [
        insightsStatePackageAPI.status,
        lazyLoadedInsights as StandalonePackage[],
        matchedPackages.length,
    ];
}
